import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { addDoc, collection, doc, deleteDoc, getCountFromServer, getDoc, getDocs, limit, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import useAuthAccess from "../hooks/useAuthAccess";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Ban,
  Bookmark,
  Check,
  Clock,
  Flag,
  Heart,
  ChevronLeft,
  ChevronRight,
  X,
  ShieldCheck,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Sparkles,
  Camera,
  CalendarDays,
  Users,
  Home as HomeIcon,
  Crown,
  Lock,
  MoreVertical,
} from "lucide-react";
import toast from "react-hot-toast";
import Navbar from "../Landing/Navbar";
import { db } from "../firebase";
import { resolveProfileRouteId } from "../utils/profileRoutes";
import { canDisplayProfile } from "../utils/profileVisibility";
import { isVerifiedProfile } from "../utils/verification";
import { isPremiumActive } from "../utils/membership";
import { getProfilePlaceholderImage } from "../utils/profilePlaceholder";
import { ProfileDetailSkeleton } from "../Components/Skeleton";
import InfoModal from "../Components/InfoModal";
import ConfirmDialog from "../Components/ConfirmDialog";

const groups = [
  {
    title: "Personal",
    Icon: Users,
    fields: ["fullName", "gender", "maritalStatus", "dateOfBirth", "timeOfBirth", "height", "religion", "caste", "gotram", "rashi", "nakshatram", "birthPlace"],
  },
  {
    title: "Education & Career",
    Icon: GraduationCap,
    fields: ["education", "collegeType", "collegeName", "profession", "companyName", "workingPlace", "annualIncome"],
  },
  {
    title: "Family",
    Icon: HomeIcon,
    fields: ["fatherName", "fatherProfession", "motherName", "motherProfession", "brothers", "marriedBrothers", "sisters", "marriedSisters", "familyFinancialStatus"],
  },
];

const partnerPrefFields = ["preferredAgeRange", "preferredLocation", "preferredEducation", "preferredProfession", "castePreference", "preferredMaritalStatus", "preferredResidenceType", "otherExpectations"];

const labels = {
  fullName: "Full Name", gender: "Gender", maritalStatus: "Marital Status", dateOfBirth: "Date of Birth", timeOfBirth: "Time of Birth", height: "Height", religion: "Religion", caste: "Caste", gotram: "Gotram", rashi: "Rashi", nakshatram: "Nakshatram", birthPlace: "Birth Place",
  education: "Education", collegeType: "College Type", collegeName: "College", profession: "Profession", companyName: "Company", workingPlace: "Working Place", annualIncome: "Income",
  fatherName: "Father", fatherProfession: "Father Profession", motherName: "Mother", motherProfession: "Mother Profession", brothers: "Brothers", marriedBrothers: "Married Brothers", sisters: "Sisters", marriedSisters: "Married Sisters", familyFinancialStatus: "Financial Status",
  preferredAgeRange: "Age Range", preferredHeight: "Height", preferredLocation: "Location", preferredEducation: "Education", preferredProfession: "Profession", castePreference: "Caste", preferredMaritalStatus: "Marital Status", preferredResidenceType: "Residence Type", otherExpectations: "Other Expectations",
  residenceType: "Residence Type", addressCountry: "Country", state: "State / Province", district: "District", cityTown: "City / Town / Village", area: "Area", pincode: "Pincode", presentResidence: "Present Residence", visaStatus: "Visa Status",
};

function getAddressFields(profile) {
  const isNri = profile?.residenceType === "NRI";
  return isNri
    ? ["residenceType", "addressCountry", "state", "cityTown", "presentResidence", "visaStatus"]
    : ["residenceType", "state", "district", "cityTown", "area", "pincode", "presentResidence"];
}

function formatDate(date) {
  if (!date) return "-";
  const d = date.toDate ? date.toDate() : new Date(date);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB").replaceAll("/", "-");
}

/* ====================================================================
   ProfileDetail
==================================================================== */
const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialRouteProfile = location.state?.profile || null;
  const { user, profile: myProfileFromAuth, loading: authLoading } = useAuthAccess();
  const queryClient = useQueryClient();
  const profileRouteId = resolveProfileRouteId(id);
  const cachedRouteProfile = queryClient.getQueryData(["profile", profileRouteId]) || null;
  // Use passed/cached profile immediately so the page renders without waiting
  const [profile, setProfile] = useState(initialRouteProfile || cachedRouteProfile);
  // Use myProfile from auth hook — no extra Firestore call needed
  const [myProfile, setMyProfile] = useState(myProfileFromAuth || null);
  // Only show skeleton if we have no bootstrapped data at all
  const [loading, setLoading] = useState(!(initialRouteProfile || cachedRouteProfile));
  const [interest, setInterest] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState("");
  const [isShortlisted, setIsShortlisted] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showSendInterestConfirm, setShowSendInterestConfirm] = useState(false);
  const [sendInterestLoading, setSendInterestLoading] = useState(false);

  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(0);
  const [showMenu, setShowMenu] = useState(false);

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate("/");
    }
  };

  const photos = useMemo(() => {
    if (!profile || profile.hidePhotos) return [];
    return (profile.imageUrls || []).filter(Boolean);
  }, [profile]);

  // Keep myProfile in sync when auth hook resolves it
  useEffect(() => {
    setMyProfile(myProfileFromAuth || null);
  }, [myProfileFromAuth]);

  const viewerHasPremiumAccess = isPremiumActive(myProfile);

  useEffect(() => {
    let cancelled = false;
    const profileId = resolveProfileRouteId(id);
    const currentUser = user;

    // Reset per-profile state but keep bootstrapped profile visible
    setAccessDenied(false);
    setAccessDeniedReason("");
    setInterest(null);
    setIsShortlisted(false);
    setIsBlocked(false);

    if (authLoading) {
      setLoading(true);
      return () => { cancelled = true; };
    }

    if (!currentUser) {
      setLoading(false);
      return () => { cancelled = true; };
    }

    const loadProfile = async () => {
      // ── Step 1: fire all independent reads in parallel ─────────────────
      const [userSnap, profileSnap] = await Promise.all([
        getDoc(doc(db, "users", currentUser.uid)).catch(() => null),
        // Only fetch from Firestore if we don't already have the profile
        (initialRouteProfile?.id === profileId || cachedRouteProfile?.id === profileId)
          ? Promise.resolve(null)
          : getDoc(doc(db, "profiles", profileId)).catch(() => null),
      ]);

      if (cancelled) return;

      // ── User doc: blocked / shortlisted lists ──────────────────────────
      const userData = userSnap?.exists() ? userSnap.data() : {};
      const currentBlockedIds = userData.blocked || [];
      const currentShortlistedIds = userData.shortlisted || [];

      // ── Target profile: use bootstrapped if available ──────────────────
      let next = initialRouteProfile?.id === profileId
        ? initialRouteProfile
        : cachedRouteProfile?.id === profileId
          ? cachedRouteProfile
          : profileSnap?.exists()
            ? { id: profileSnap.id, ...profileSnap.data() }
            : null;

      if (!next) {
        if (!cancelled) {
          setProfile(null);
          setAccessDenied(false);
          setAccessDeniedReason("");
          setLoading(false);
        }
        return;
      }

      if (!canDisplayProfile(next, viewerHasPremiumAccess) || currentBlockedIds.includes(next.id)) {
        if (!cancelled) {
          setProfile(null);
          setAccessDenied(true);
          setAccessDeniedReason(
            next.profileVisibility === "premium"
              ? "premium"
              : next.profileVisibility === "hidden" || next.adminHidden
                ? "hidden"
                : currentBlockedIds.includes(next.id)
                  ? "blocked"
                  : "restricted"
          );
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setProfile(next);
        setAccessDenied(false);
        setAccessDeniedReason("");
        setActiveIndex(0);
        setIsShortlisted(currentShortlistedIds.includes(next.id));
        setIsBlocked(false);
        setLoading(false);
      }

      // Cache for other pages
      try { queryClient.setQueryData(["profile", next.id], next); } catch { /* ignore */ }

      // ── Step 2: fire secondary reads in parallel (non-blocking for UI) ─
      const pairId = [currentUser.uid, next.id].sort().join("_");
      const [interestSnap] = await Promise.all([
        // Interest status
        (currentUser.uid !== next.id
          ? getDoc(doc(db, "interests", pairId)).catch(() => null)
          : Promise.resolve(null)),
        // View tracking — skip if the viewer has set their profile to hidden
        // (hidden users browse anonymously and won't appear in "who viewed")
        (currentUser.uid !== next.id && myProfile?.profileVisibility !== "hidden"
          ? setDoc(
              doc(db, "profileViews", `${currentUser.uid}_${next.id}`),
              { viewerId: currentUser.uid, viewedId: next.id, viewedAt: serverTimestamp() },
              { merge: true }
            ).catch(() => {})
          : Promise.resolve()),
      ]);

      if (cancelled) return;
      setInterest(interestSnap?.exists() ? { id: interestSnap.id, ...interestSnap.data() } : null);
    };

    loadProfile().catch((err) => {
      if (!cancelled) {
        console.warn("Could not load profile details:", err.message);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [id, user, authLoading, queryClient, viewerHasPremiumAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const FREE_INTEREST_LIMIT = 5;

  const sendInterest = async () => {
    if (!user || !profile) return;

    // Check if user is verified
    if (!isPremiumActive(myProfile) && !isVerifiedProfile(myProfile)) {
      setShowVerifyModal(true);
      return;
    }

    // Check free user limit - prefer cached counts
    if (!isPremiumActive(myProfile)) {
      try {
        const cached = queryClient.getQueryData(["interestCounts", user.uid]);
        let sentCount = cached && typeof cached.sent === "number" ? cached.sent : null;

        if (sentCount == null) {
          const sentQuery = query(collection(db, "interests"), where("fromUserId", "==", user.uid));
          const sentSnap = await getCountFromServer(sentQuery).catch(async () => {
            const fallbackSnap = await getDocs(query(sentQuery, limit(FREE_INTEREST_LIMIT + 1)));
            return { data: () => ({ count: fallbackSnap.size }) };
          });
          sentCount = sentSnap.data ? sentSnap.data().count || 0 : sentSnap.size || 0;
        }

        if (sentCount >= FREE_INTEREST_LIMIT) {
          toast("You've used all 5 free interests. Upgrade to Premium to send unlimited interests.", { icon: "👑" });
          return;
        }
      } catch {
        // ignore count check errors
      }
    }

    const pairId = [user.uid, profile.id].sort().join("_");
    try {
      await setDoc(doc(db, "interests", pairId), {
        pairId, fromUserId: user.uid, toUserId: profile.id,
        status: "pending", createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      setInterest({ id: pairId, pairId, fromUserId: user.uid, toUserId: profile.id, status: "pending" });
      queryClient.setQueryData(["interestCounts", user.uid], (current) => {
        const prev = current || { sent: 0, received: 0 };
        return { ...prev, sent: (typeof prev.sent === "number" ? prev.sent : 0) + 1 };
      });
      toast.success("Interest sent.");
    } catch (error) {
      toast.error(error.code === "permission-denied" ? "Interest already exists." : error.message || "Could not send interest.");
    }
  };

  const confirmSendInterest = async () => {
    setShowSendInterestConfirm(false);
    setSendInterestLoading(true);
    try {
      await sendInterest();
    } finally {
      setSendInterestLoading(false);
    }
  };

  const withdrawInterest = async () => {
    if (!user || !profile || !interest) return;
    try {
      await deleteDoc(doc(db, "interests", interest.id));
      setInterest(null);
      toast.success("Interest withdrawn.");
    } catch (error) {
      toast.error(error.message || "Could not withdraw interest.");
    }
  };

  const acceptInterest = async () => {
    if (!user || !interest) return;
    try {
      await updateDoc(doc(db, "interests", interest.id), {
        status: "accepted",
        updatedAt: serverTimestamp(),
      });
      setInterest({ ...interest, status: "accepted" });
      toast.success("Interest accepted!");
    } catch (error) {
      toast.error(error.message || "Could not accept interest.");
    }
  };

  const declineInterest = async () => {
    if (!user || !interest) return;
    try {
      await updateDoc(doc(db, "interests", interest.id), {
        status: "declined",
        updatedAt: serverTimestamp(),
      });
      setInterest({ ...interest, status: "declined" });
      toast.success("Interest declined.");
    } catch (error) {
      toast.error(error.message || "Could not decline interest.");
    }
  };

  const interestButton = () => {
    const iSent = interest?.fromUserId === user?.uid;
    const iReceived = interest?.toUserId === user?.uid;

    // Interest accepted — show Accepted status
    if (interest?.status === "accepted")
      return (
        <button disabled className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-emerald-50 py-3 text-[13px] font-semibold text-emerald-700">
          <Check size={15} /> Interest Accepted
        </button>
      );

    // Interest declined
    if (interest?.status === "declined")
      return <button disabled className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-rose-50 py-3 text-[14px] font-semibold text-rose-700"><Ban size={16} /> Interest declined</button>;

    // I SENT the interest — show Sent + Withdraw
    if (interest?.status === "pending" && iSent)
      return (
        <div className="grid grid-cols-2 gap-2">
          <button disabled className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-amber-50 py-3 text-[13px] font-semibold text-amber-700">
            <Clock size={15} /> Sent
          </button>
          <button
            onClick={withdrawInterest}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-white py-3 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-50"
          >
            <X size={15} /> Withdraw
          </button>
        </div>
      );

    // I RECEIVED the interest — show Accept + Decline
    if (interest?.status === "pending" && iReceived)
      return (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={acceptInterest}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3 text-[13px] font-semibold text-white transition hover:bg-emerald-700"
          >
            <Check size={15} /> Accept
          </button>
          <button
            onClick={declineInterest}
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-white py-3 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-50"
          >
            <X size={15} /> Decline
          </button>
        </div>
      );

    // No interest yet — show Send Interest
    return (
      <button onClick={() => setShowSendInterestConfirm(true)} className="btn-primary w-full py-3 text-[14px]">
        <Heart size={16} /> Send Interest
      </button>
    );
  };

  const addListItem = async (field) => {
    if (!user || !profile) return;
    const userRef = doc(db, "users", user.uid);
    const snapshot = await getDoc(userRef);
    const data = snapshot.exists() ? snapshot.data() : { uid: user.uid, role: "user" };
    const current = data[field] || [];
    if (current.includes(profile.id)) {
      toast(field === "shortlisted" ? "Already shortlisted." : "Already blocked.", { icon: "ℹ️" });
      return;
    }
    const updateData = { [field]: [...current, profile.id], updatedAt: serverTimestamp() };
    if (snapshot.exists()) {
      await updateDoc(userRef, updateData);
    } else {
      await setDoc(userRef, { uid: user.uid, role: "user", ...updateData }, { merge: true });
    }

    try {
      queryClient.setQueryData(["userDoc", user.uid], (currentDoc) => {
        const nextDoc = currentDoc || { blocked: [], shortlisted: [] };
        const nextList = Array.isArray(nextDoc[field]) ? nextDoc[field] : [];
        if (nextList.includes(profile.id)) return nextDoc;
        return { ...nextDoc, [field]: [...nextList, profile.id] };
      });
      queryClient.invalidateQueries({ queryKey: ["matches"] });
      queryClient.invalidateQueries({ queryKey: ["dashboardRecommendations"] });
    } catch {
      // ignore cache failures
    }

    if (field === "shortlisted") {
      setIsShortlisted(true);
      toast.success("Profile shortlisted.");
    } else {
      setIsBlocked(true);
      toast.success("Profile blocked.");
    }
  };

  const submitReport = async () => {
    if (!user || !profile || !reportReason) return;
    setReportSubmitting(true);
    try {
      await addDoc(collection(db, "reports"), {
        reportedUserId: profile.id,
        reportedUserName: profile.fullName || "",
        reportedByUserId: user.uid,
        reason: reportReason,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      setShowReportModal(false);
      setReportReason("");
      toast.success("Report submitted. Our team will review it.");
    } catch (err) {
      toast.error(err.message || "Could not submit report.");
    } finally {
      setReportSubmitting(false);
    }
  };

  /* --------- Lightbox keyboard navigation --------- */
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const nextLightbox = useCallback(() => {
    if (lightboxIndex === null) return;
    if (!isPremiumActive(myProfile)) return;
    setLightboxIndex((i) => Math.min(i + 1, photos.length - 1));
  }, [lightboxIndex, photos, myProfile]);
  const prevLightbox = useCallback(() => {
    if (lightboxIndex === null) return;
    if (!isPremiumActive(myProfile)) return;
    setLightboxIndex((i) => Math.max(i - 1, 0));
  }, [lightboxIndex, myProfile]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") nextLightbox();
      else if (e.key === "ArrowLeft") prevLightbox();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIndex, closeLightbox, nextLightbox, prevLightbox]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container pt-4 pb-app-nav sm:pt-6 lg:pt-8">
        {/* Top bar */}
        <div className="mb-5 flex items-center justify-between gap-3">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-2xl border border-sand-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-ink-700 shadow-soft transition hover:border-brand-600 hover:text-brand-600"
          >
            <ArrowLeft size={16} /> Back
          </button>

          {/* Three-dot menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu((v) => !v)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-sand-200 bg-white text-ink-600 shadow-soft transition hover:bg-sand-50"
              aria-label="More options"
            >
              <MoreVertical size={18} />
            </button>
            <AnimatePresence>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -4 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-elevated"
                  >
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        addListItem("blocked");
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-[13.5px] font-semibold text-rose-600 transition hover:bg-rose-50"
                    >
                      <Ban size={15} /> Block this profile
                    </button>
                    <div className="border-t border-sand-100" />
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setShowReportModal(true);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-[13.5px] font-semibold text-amber-700 transition hover:bg-amber-50"
                    >
                      <Flag size={15} /> Report this profile
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>

        {loading ? (
          <ProfileDetailSkeleton />
        ) : accessDenied ? (
          <div className="card p-8 text-center text-ink-500">
            <ShieldCheck className="mx-auto text-brand-600" />
            <p className="mt-3">
              {accessDeniedReason === "premium"
                ? "This profile is visible to Premium members only."
                : accessDeniedReason === "blocked"
                  ? "This profile is not available to you."
                  : "This profile is hidden or no longer available."}
            </p>
          </div>
        ) : !profile ? (
          <div className="card p-8 text-center text-ink-500">Profile not found.</div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[460px_minmax(0,1fr)] xl:gap-8">
            {/* ============= LEFT — Hero gallery + actions ============= */}
            <div className="space-y-4">
              {/* Hero */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-[28px] border border-sand-200 bg-white shadow-elevated"
              >
                {photos.length === 0 ? (
                  <img
                    src={getProfilePlaceholderImage(profile.gender)}
                    alt={profile.fullName || "Profile"}
                    className="aspect-[4/5] w-full object-cover object-center"
                  />
                ) : (
                  <div
                    className="relative aspect-[4/5] w-full overflow-hidden touch-pan-y"
                    onClick={() => {
                      if (!isPremiumActive(myProfile) && activeIndex > 0) return;
                      setLightboxIndex(activeIndex);
                    }}
                  >
                    <AnimatePresence initial={false} mode="wait">
                      <motion.img
                        key={activeIndex}
                        src={photos[activeIndex]}
                        alt={profile.fullName}
                        initial={{ x: swipeDirection >= 0 ? "40%" : "-40%", opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: swipeDirection >= 0 ? "-40%" : "40%", opacity: 0 }}
                        transition={{ type: "spring", stiffness: 350, damping: 32, mass: 0.7 }}
                        drag={photos.length > 1 && isPremiumActive(myProfile) ? "x" : false}
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.4}
                        onDragEnd={(e, { offset, velocity }) => {
                          const swipeForce = Math.abs(offset.x) * velocity.x;
                          // Swipe left → next photo (only if not last)
                          if ((offset.x < -50 || swipeForce < -5000) && activeIndex < photos.length - 1) {
                            setSwipeDirection(1);
                            setActiveIndex((i) => i + 1);
                          }
                          // Swipe right → previous photo (only if not first)
                          else if ((offset.x > 50 || swipeForce > 5000) && activeIndex > 0) {
                            setSwipeDirection(-1);
                            setActiveIndex((i) => i - 1);
                          }
                        }}
                        className="absolute inset-0 h-full w-full cursor-grab object-cover object-center active:cursor-grabbing"
                        style={{ touchAction: "pan-y" }}
                      />
                    </AnimatePresence>

                    {/* Dot indicators removed from here — moved to name overlay */}
                  </div>
                )}

                {/* gradient + name overlay */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 via-black/15 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  {/* Swipe hint — centered above badges */}
                  {photos.length > 1 && isPremiumActive(myProfile) && activeIndex === 0 && (
                    <motion.div
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 0 }}
                      transition={{ delay: 2.5, duration: 0.8 }}
                      className="pointer-events-none mb-2 flex justify-center sm:hidden"
                    >
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur">
                        <ChevronLeft size={12} />
                        Swipe for more photos
                        <ChevronRight size={12} />
                      </span>
                    </motion.div>
                  )}

                  {/* Dot indicators — centered */}
                  {photos.length > 1 && isPremiumActive(myProfile) && (
                    <div className="mb-2 flex justify-center gap-1.5">
                      {photos.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSwipeDirection(idx > activeIndex ? 1 : -1);
                            setActiveIndex(idx);
                          }}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            idx === activeIndex
                              ? "w-5 bg-white shadow-md"
                              : "w-2 bg-white/50"
                          }`}
                          aria-label={`Photo ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    {isVerifiedProfile(profile) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                        <ShieldCheck size={11} /> Verified
                      </span>
                    )}
                    {profile.membership === "premium" && isPremiumActive(profile) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-900">
                        Premium
                      </span>
                    )}
                  </div>
                  <h1 className="mt-1.5 font-display text-2xl font-bold leading-tight sm:text-3xl">
                    {profile.fullName}
                  </h1>
                  {profile.kmProfileId && (
                    <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-white/25 bg-white/10 px-2 py-0.5 text-[10.5px] font-semibold backdrop-blur">
                      {profile.kmProfileId}
                    </span>
                  )}
                  <p className="mt-1 truncate text-[13px] text-white/85">
                    {profile.profession ? `${profile.profession} • ` : ""}
                    {profile.presentResidence || profile.workingPlace || "—"}
                  </p>
                </div>

                {/* Photo nav arrows */}
                {photos.length > 1 && isPremiumActive(myProfile) && (
                  <>
                    {activeIndex > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSwipeDirection(-1);
                          setActiveIndex((i) => i - 1);
                        }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-ink-800 shadow-soft backdrop-blur transition hover:bg-white hidden sm:inline-flex"
                        aria-label="Previous photo"
                      >
                        <ChevronLeft size={18} />
                      </button>
                    )}
                    {activeIndex < photos.length - 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSwipeDirection(1);
                          setActiveIndex((i) => i + 1);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-ink-800 shadow-soft backdrop-blur transition hover:bg-white hidden sm:inline-flex"
                        aria-label="Next photo"
                      >
                        <ChevronRight size={18} />
                      </button>
                    )}

                    <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                      <Camera size={12} /> {activeIndex + 1} / {photos.length}
                    </div>
                  </>
                )}
                {/* Free user: show photo count + lock */}
                {photos.length > 1 && !isPremiumActive(myProfile) && (
                  <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/45 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
                    <Lock size={11} /> +{photos.length - 1} photos
                  </div>
                )}

              </motion.div>

              {/* Thumbnails */}
              {photos.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {photos.map((url, idx) => {
                    const isLocked = idx > 0 && !isPremiumActive(myProfile);
                    return (
                      <button
                        key={url + idx}
                        type="button"
                        onClick={() => {
                          if (isLocked) return;
                          setSwipeDirection(idx > activeIndex ? 1 : -1);
                          setActiveIndex(idx);
                        }}
                        className={`relative aspect-square overflow-hidden rounded-xl border-2 transition ${
                          idx === activeIndex
                            ? "border-brand-600 shadow-soft"
                            : "border-transparent opacity-70 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={url}
                          alt={`${profile.fullName} ${idx + 1}`}
                          className={`h-full w-full object-cover object-center ${isLocked ? "blur-[6px] scale-110" : ""}`}
                        />
                        {isLocked && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <Lock size={14} className="text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {!isPremiumActive(myProfile) && photos.length > 1 && (
                    <Link
                      to="/settings/membership"
                      className="flex aspect-square items-center justify-center rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 text-center"
                    >
                      <div>
                        <Crown size={14} className="mx-auto text-amber-600" />
                        <p className="mt-0.5 text-[8px] font-bold text-amber-700">Upgrade</p>
                      </div>
                    </Link>
                  )}
                </div>
              )}

              {/* Action card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="card p-5"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="section-subtle">Connect</p>
                    <h2 className="mt-1 font-display text-lg font-bold text-ink-800">Show your interest</h2>
                  </div>
                  <Heart className="text-brand-600" />
                </div>

                <div className="mt-4 space-y-2">
                  {interestButton()}
                  {isShortlisted ? (
                    <button disabled className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-amber-50 py-3 text-[13.5px] font-semibold text-amber-700">
                      <Bookmark size={16} /> Shortlisted
                    </button>
                  ) : (
                    <button
                      onClick={() => addListItem("shortlisted")}
                      className="btn-outline w-full py-3"
                    >
                      <Bookmark size={16} /> Shortlist
                    </button>
                  )}
                  {isBlocked && (
                    <button disabled className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-rose-50 py-3 text-[13.5px] font-semibold text-rose-600">
                      <Ban size={16} /> Blocked
                    </button>
                  )}
                </div>
              </motion.div>
            </div>

            {/* ============= RIGHT — Details ============= */}
            <div className="space-y-5">
              {/* Snapshot row */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid gap-2 grid-cols-2 lg:grid-cols-4"
              >
                <Snapshot Icon={CalendarDays} label="Born" value={formatDate(profile.dateOfBirth)} />
                <Snapshot Icon={Briefcase} label="Works as" value={profile.profession} />
                <Snapshot Icon={GraduationCap} label="Education" value={profile.education} />
                <Snapshot Icon={MapPin} label="Lives in" value={profile.presentResidence || profile.state} />
              </motion.div>

              {/* About */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-5 sm:p-6"
              >
                <h2 className="font-display text-lg font-bold text-ink-800">About</h2>
                <p className="mt-3 leading-7 text-ink-500">
                  {profile.aboutMe || "About not added."}
                </p>
              </motion.div>

              {/* Detail groups */}
              {groups.map(({ title, Icon, fields }) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35 }}
                  className="card p-5 sm:p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                      <Icon size={18} />
                    </div>
                    <h2 className="font-display text-lg font-bold text-ink-800">{title}</h2>
                  </div>
                  <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                    {fields.filter((field) => {
                      const val = profile[field];
                      return val && val !== "-" && val !== "0" && !(Array.isArray(val) && val.length === 0);
                    }).map((field) => (
                      <div key={field}>
                        <dt className="text-[11.5px] font-semibold uppercase tracking-wider text-ink-400">
                          {labels[field]}
                        </dt>
                        <dd className="mt-0.5 break-words text-[14px] font-semibold text-ink-800">
                          {field === "dateOfBirth" ? formatDate(profile[field]) : (profile[field] || "-")}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </motion.div>
              ))}

              {/* Address */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                className="card p-5 sm:p-6"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                    <MapPin size={18} />
                  </div>
                  <h2 className="font-display text-lg font-bold text-ink-800">Address</h2>
                </div>
                <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                  {getAddressFields(profile).map((field) => (
                    <div key={field}>
                      <dt className="text-[11.5px] font-semibold uppercase tracking-wider text-ink-400">
                        {labels[field]}
                      </dt>
                      <dd className="mt-0.5 break-words text-[14px] font-semibold text-ink-800">
                        {profile[field] || "-"}
                      </dd>
                    </div>
                  ))}
                </dl>
              </motion.div>

              {/* Partner Preferences */}
              {partnerPrefFields.some((f) => profile[f] && profile[f] !== "-" && profile[f] !== "Any") && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card p-5 sm:p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                      <Sparkles size={18} />
                    </div>
                    <h2 className="font-display text-lg font-bold text-ink-800">Partner Preferences</h2>
                  </div>
                  <dl className="mt-5 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                    {partnerPrefFields.filter((field) => {
                      const val = profile[field];
                      return val && val !== "-" && val !== "Any";
                    }).map((field) => (
                      <div key={field}>
                        <dt className="text-[11.5px] font-semibold uppercase tracking-wider text-ink-400">
                          {labels[field]}
                        </dt>
                        <dd className="mt-0.5 break-words text-[14px] font-semibold text-ink-800">
                          {profile[field]}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </motion.div>
              )}

              {/* Contact Details — state-based display */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-5 sm:p-6"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    interest?.status === "accepted" ? "bg-emerald-50 text-emerald-600" : "bg-sand-100 text-ink-400"
                  }`}>
                    <Phone size={18} />
                  </div>
                  <h2 className="font-display text-lg font-bold text-ink-800">Contact details</h2>
                </div>

                {/* No interest sent */}
                {!interest && (
                  <div className="mt-4 rounded-2xl border border-sand-200 bg-sand-50 p-4 text-center">
                    <Lock size={20} className="mx-auto text-ink-400" />
                    <p className="mt-2 text-[13.5px] font-semibold text-ink-700">
                      Send interest to connect
                    </p>
                    <p className="mt-1 text-[12px] text-ink-400">
                      Contact details will be available after mutual interest acceptance.
                    </p>
                  </div>
                )}

                {/* Interest pending */}
                {interest?.status === "pending" && (
                  <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-center">
                    <Clock size={20} className="mx-auto text-amber-600" />
                    <p className="mt-2 text-[13.5px] font-semibold text-amber-800">
                      Waiting for acceptance
                    </p>
                    <p className="mt-1 text-[12px] text-amber-600">
                      Contact details will be available once they accept your interest.
                    </p>
                  </div>
                )}

                {/* Interest declined */}
                {interest?.status === "declined" && (
                  <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-center">
                    <Ban size={20} className="mx-auto text-rose-500" />
                    <p className="mt-2 text-[13.5px] font-semibold text-rose-700">
                      Interest was declined
                    </p>
                    <p className="mt-1 text-[12px] text-rose-500">
                      Contact details are not available.
                    </p>
                  </div>
                )}

                {/* Interest accepted — FREE user */}
                {interest?.status === "accepted" && !isPremiumActive(myProfile) && (
                  <div className="mt-4">
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-center">
                      <p className="text-[12.5px] font-semibold text-emerald-700">Interest accepted!</p>
                    </div>
                    <div className="mt-3 rounded-2xl border border-sand-200 bg-sand-50 p-4">
                      <dl className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Mobile</dt>
                          <dd className="mt-0.5 text-[14px] font-semibold text-ink-400 blur-[4px] select-none">
                            {(profile.mobileNumber || "9876543210").replace(/./g, "•")}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Alternate</dt>
                          <dd className="mt-0.5 text-[14px] font-semibold text-ink-400 blur-[4px] select-none">
                            {(profile.alternateNumber || "9876543210").replace(/./g, "•")}
                          </dd>
                        </div>
                      </dl>
                      <div className="mt-4 text-center">
                        <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-[11.5px] font-semibold text-amber-700">
                          <Lock size={12} /> Upgrade to view contact numbers
                        </div>
                        <Link
                          to="/settings/membership"
                          className="btn-primary mt-3 w-full py-3"
                        >
                          <Crown size={15} /> Upgrade to Premium
                        </Link>
                      </div>
                    </div>
                  </div>
                )}

                {/* Interest accepted — PREMIUM user */}
                {interest?.status === "accepted" && isPremiumActive(myProfile) && (
                  <div className="mt-4">
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2.5">
                      <p className="text-[12.5px] font-semibold text-emerald-700">✅ Interest accepted — contact visible</p>
                    </div>
                    <dl className="mt-3 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                      <div>
                        <dt className="text-[11.5px] font-semibold uppercase tracking-wider text-ink-400">Mobile Number</dt>
                        <dd className="mt-0.5 text-[14px] font-semibold text-ink-800">
                          {profile.mobileNumber || profile.phone || "Not available"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[11.5px] font-semibold uppercase tracking-wider text-ink-400">Alternate Number</dt>
                        <dd className="mt-0.5 text-[14px] font-semibold text-ink-800">
                          {profile.alternateNumber || "Not available"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                )}
              </motion.div>

              {/* Interests */}
              {!!profile.interests?.length && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="card p-5 sm:p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                      <Sparkles size={18} />
                    </div>
                    <h2 className="font-display text-lg font-bold text-ink-800">Interests</h2>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {profile.interests.map((it) => (
                      <span key={it} className="chip">{it}</span>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ============= LIGHTBOX ============= */}
      <AnimatePresence>
        {lightboxIndex !== null && photos[lightboxIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
            onClick={closeLightbox}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[12px] font-semibold text-white backdrop-blur">
              <Camera size={13} /> {lightboxIndex + 1} / {photos.length}
            </div>

            {photos.length > 1 && isPremiumActive(myProfile) && (
              <>
                {lightboxIndex > 0 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
                    className="absolute left-4 top-1/2 z-10 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                    aria-label="Previous"
                  >
                    <ChevronLeft size={22} />
                  </button>
                )}
                {lightboxIndex < photos.length - 1 && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
                    className="absolute right-4 top-1/2 z-10 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                    aria-label="Next"
                  >
                    <ChevronRight size={22} />
                  </button>
                )}
              </>
            )}

            <motion.img
              key={photos[lightboxIndex]}
              src={photos[lightboxIndex]}
              alt={`Photo ${lightboxIndex + 1}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25 }}
              drag={photos.length > 1 && isPremiumActive(myProfile) ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.4}
              onDragEnd={(e, { offset, velocity }) => {
                const swipeForce = Math.abs(offset.x) * velocity.x;
                if ((offset.x < -50 || swipeForce < -5000) && lightboxIndex < photos.length - 1) {
                  nextLightbox();
                } else if ((offset.x > 50 || swipeForce > 5000) && lightboxIndex > 0) {
                  prevLightbox();
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-2xl shadow-2xl cursor-grab active:cursor-grabbing"
              style={{ maxHeight: "85vh", maxWidth: "90vw", width: "auto", height: "85vh", objectFit: "contain", touchAction: "pan-y" }}
            />

            {/* Dot indicators in lightbox */}
            {photos.length > 1 && isPremiumActive(myProfile) && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-x-0 bottom-[100px] flex justify-center gap-1.5 z-10"
              >
                {photos.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setLightboxIndex(idx)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      idx === lightboxIndex
                        ? "w-6 bg-white shadow-md"
                        : "w-2.5 bg-white/50"
                    }`}
                    aria-label={`Photo ${idx + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Thumbnail strip — premium only */}
            {photos.length > 1 && isPremiumActive(myProfile) && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute inset-x-0 bottom-4 flex justify-center"
              >
                <div className="flex max-w-full gap-2 overflow-x-auto rounded-2xl bg-white/10 p-2 backdrop-blur">
                  {photos.map((u, i) => (
                    <button
                      key={u + i}
                      onClick={() => setLightboxIndex(i)}
                      className={`h-12 w-12 shrink-0 overflow-hidden rounded-xl border-2 transition ${
                        i === lightboxIndex ? "border-white" : "border-transparent opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img src={u} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <InfoModal
        open={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        Icon={ShieldCheck}
        tone="warning"
        title="Verification required"
        description="Your profile needs to be verified by admin before you can send interests. Please wait for verification. This usually takes 24-48 hours."
        okLabel="OK"
      />

      {/* Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !reportSubmitting && setShowReportModal(false)}
            className="fixed inset-0 z-[90] flex items-end justify-center bg-ink-900/50 px-4 backdrop-blur-sm sm:items-center"
          >
            <motion.div
              initial={{ y: 32, opacity: 0, scale: 0.97 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-3xl"
            >
              <div className="flex items-start gap-4 px-6 pt-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                  <Flag size={22} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-ink-800">Report profile</h3>
                  <p className="mt-1 text-[13px] text-ink-500">Why are you reporting {profile?.fullName || "this profile"}?</p>
                </div>
                <button onClick={() => setShowReportModal(false)} className="btn-icon h-9 w-9">
                  <X size={16} />
                </button>
              </div>

              <div className="px-6 py-4 space-y-2">
                {[
                  "Fake profile / scam",
                  "Inappropriate photos",
                  "Harassment or abusive behavior",
                  "Already married",
                  "Wrong information",
                  "Other",
                ].map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setReportReason(reason)}
                    className={`flex w-full items-center gap-3 rounded-2xl border-2 p-3 text-left text-[13.5px] font-semibold transition ${
                      reportReason === reason
                        ? "border-amber-500 bg-amber-50 text-amber-800"
                        : "border-sand-200 bg-white text-ink-700 hover:border-amber-200"
                    }`}
                  >
                    {reportReason === reason && <Check size={14} className="text-amber-600" />}
                    {reason}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-sand-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setShowReportModal(false)}
                  disabled={reportSubmitting}
                  className="btn-ghost py-3"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={!reportReason || reportSubmitting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-[14px] font-semibold text-white shadow-soft transition hover:bg-amber-600 active:scale-[0.98] disabled:opacity-50"
                >
                  {reportSubmitting ? "Submitting..." : "Submit report"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <ConfirmDialog
        open={showSendInterestConfirm}
        title="Send interest?"
        description={profile ? `Show your interest to ${profile.fullName}?` : "Show your interest to this profile?"}
        Icon={Heart}
        tone="brand"
        confirmLabel="Send"
        loading={sendInterestLoading}
        onClose={() => !sendInterestLoading && setShowSendInterestConfirm(false)}
        onConfirm={confirmSendInterest}
      />
    </div>
  );
};

const Snapshot = ({ Icon, label, value }) => (
  <div className="card flex items-center gap-3 p-3 sm:p-4">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
      <Icon size={18} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{label}</p>
      <p className="mt-0.5 break-words text-[13px] font-semibold leading-tight text-ink-800">{value || "-"}</p>
    </div>
  </div>
);

export default ProfileDetail;
